import React, { useState } from 'react';
import { Icons as I } from '../pack-icons';
import { BrandIcons } from '../pack-icons/brands';

/**
 * ActiveJobsWidget — shown when a batch is being generated.
 * job: { project, current, total, step, started?, eta?, items?: [{label,status,meta}], posts?: [{slot, platform, time, status}] }
 *
 * `items` and `posts` are optional; component degrades to just the progress bar if absent.
 */
export default function ActiveJobsWidget({ job, onPause, onOpenBatch }) {
  const [tab, setTab] = useState('steps');
  if (!job) return null;
  const total = Math.max(1, job.total || 1);
  const current = Math.max(0, Math.min(total, job.current || 0));
  const pct = Math.round((current / total) * 100);
  const hasItems = job.items?.length > 0;
  const hasPosts = job.posts?.length > 0;

  return (
    <section className="ajw">
      <div className="ajw-bg" />

      <div className="ajw-head">
        <div className="ajw-mark">
          <span className="ajw-spin"><I.sparkles size={18} /></span>
        </div>
        <div className="ajw-head-l">
          <div className="ajw-head-row">
            <h3 className="ajw-title">Generating {total} post{total === 1 ? '' : 's'}</h3>
            <span className="pill pill-accent"><span className="dot pulse" /> Live</span>
          </div>
          <div className="ajw-sub">
            {job.project && <span>{job.project}</span>}
            {job.started && <><span className="ajw-sep">·</span><span className="mono">Started {job.started}</span></>}
            {job.eta && <><span className="ajw-sep">·</span><span className="mono">ETA {job.eta}</span></>}
          </div>
        </div>
        <div className="ajw-actions">
          {onPause && (
            <button className="btn btn-ghost btn-sm" onClick={onPause} type="button">
              <I.pause size={12} /> Pause
            </button>
          )}
          <button className="btn btn-ghost btn-sm btn-icon" type="button" aria-label="More">
            <I.dots size={14} />
          </button>
        </div>
      </div>

      <div className="ajw-progress">
        {job.step && (
          <div className="ajw-step">
            <span className="ajw-step-l">Current step</span>
            <span className="ajw-step-v">
              <span className="dot pulse" style={{ background: 'var(--accent)' }} />{job.step}
            </span>
          </div>
        )}
        <div className="ajw-prog-track">
          <div className="ajw-prog-fill" style={{ width: pct + '%' }} />
        </div>
        <div className="ajw-prog-meta">
          <span>
            <b>{current}</b> <span className="muted">of</span> <b>{total}</b> posts
          </span>
          <span className="mono">{pct}%</span>
        </div>
      </div>

      {(hasItems || hasPosts) && (
        <div className="ajw-tabs">
          {hasItems && (
            <button className={tab === 'steps' ? 'is-active' : ''} onClick={() => setTab('steps')} type="button">
              Workflow steps
            </button>
          )}
          {hasPosts && (
            <button className={tab === 'posts' ? 'is-active' : ''} onClick={() => setTab('posts')} type="button">
              Post tiles ({total})
            </button>
          )}
        </div>
      )}

      {tab === 'steps' && hasItems && (
        <div className="ajw-steps">
          {job.items.map((it, i) => (
            <div key={i} className={`ajw-step-row ajw-${it.status}`}>
              <span className="ajw-step-ico">
                {it.status === 'done' && <I.check size={11} />}
                {it.status === 'doing' && <span className="dot pulse" style={{ background: 'currentColor' }} />}
                {it.status === 'queued' && <span className="dot" style={{ background: 'currentColor', opacity: 0.5 }} />}
              </span>
              <span className="ajw-step-name">{it.label}</span>
              {it.meta && <span className="ajw-step-meta mono">{it.meta}</span>}
            </div>
          ))}
        </div>
      )}

      {tab === 'posts' && hasPosts && (
        <div className="ajw-posts">
          {job.posts.map((p, i) => (
            <div key={i} className={`ajw-post ajw-post-${p.status}`}>
              <span className="ajw-post-ic">{BrandIcons[p.platform]?.({ size: 11 })}</span>
              <span className="ajw-post-no mono">#{p.slot}</span>
              <span className="ajw-post-time mono">{p.time}</span>
              {p.status === 'done' && <I.check size={11} className="ajw-post-check" />}
              {p.status === 'doing' && <span className="dot pulse" style={{ background: 'var(--accent)' }} />}
            </div>
          ))}
        </div>
      )}

      {onOpenBatch && (
        <div className="ajw-foot" style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 12 }}>
          <button className="btn btn-soft btn-sm" onClick={onOpenBatch} type="button">
            Open batch <I.arrowRight size={12} />
          </button>
        </div>
      )}
    </section>
  );
}
